/**
 * Product-line helpers for customize wizard (BRD Wave B).
 * productLine is canonical; journeyType kept in sync for older step code.
 */
import {
  coerceBookingLocations,
  getCountriesForProductLine,
  getDefaultDestinationCountry,
  getDepartureCities,
  getDestinationAirports,
  getOriginAirports,
  getStayCitiesForSelection,
  cityToAirportCode,
} from './locationHierarchy';

export {
  coerceBookingLocations,
  getCountriesForProductLine,
  getDefaultDestinationCountry,
  getDepartureCities,
  getDestinationAirports,
  getOriginAirports,
  getStayCitiesForSelection,
  cityToAirportCode,
};

export const PRODUCT_LINES = [
  {
    value: 'Umrah',
    journeyType: 'umrah',
    label: 'Umrah',
    detail: 'Makkah & Madina pilgrimage',
  },
  {
    value: 'International',
    journeyType: 'travel',
    label: 'International',
    detail: 'Dubai, Turkey, holidays abroad',
  },
  {
    value: 'Domestic',
    journeyType: 'travel',
    label: 'Domestic Pakistan',
    detail: 'Hunza, Skardu, Swat, cities',
  },
  {
    value: 'Local',
    journeyType: 'travel',
    label: 'Local / Weekend',
    detail: 'Short trips & day getaways',
  },
];

export const resolveProductLine = (data = {}) => {
  if (data.productLine && PRODUCT_LINES.some((p) => p.value === data.productLine)) {
    return data.productLine;
  }
  if ((data.journeyType || '') === 'umrah') return 'Umrah';
  return 'International';
};

export const isUmrahLine = (data) => resolveProductLine(data) === 'Umrah';

/** What service checkboxes / panels are allowed for this product line */
export const getServiceVisibility = (productLine) => {
  const line = productLine || 'International';
  return {
    showFlight: true,
    showHotel: true,
    showTransport: true,
    showHomePickup: true,
    showVisa: line === 'Umrah' || line === 'International',
    showCarRental: line !== 'Umrah', // optional for Umrah but usually not primary
    showZiaraat: line === 'Umrah',
    defaultIncludeFlight: line === 'International',
    defaultIncludeVisa: line === 'Umrah',
    defaultIncludeZiaraat: line === 'Umrah',
    defaultIncludeHotel: line !== 'Local',
    defaultIncludeCarRental: line === 'Domestic' || line === 'Local',
    defaultIncludeHomePickup: line === 'Local',
  };
};

export const getStayLocationChoices = (productLine, destinationCountry) =>
  getStayCitiesForSelection(productLine, destinationCountry);

export const getStayStopLabels = (productLine) => {
  if (productLine === 'Umrah') {
    return {
      first: 'First Entry',
      second: 'Second Entry',
      third: 'Third Entry (Optional)',
      defaults: { first: 'Makkah', second: 'Madina', third: 'Makkah' },
    };
  }
  if (productLine === 'Local') {
    return {
      first: 'Main stay',
      second: 'Extra night spot (Optional)',
      third: 'Third stop (Optional)',
      defaults: { first: 'Murree', second: 'Islamabad', third: 'Lahore' },
    };
  }
  if (productLine === 'Domestic') {
    return {
      first: 'Main Destination',
      second: 'Second City (Optional)',
      third: 'Third City (Optional)',
      defaults: { first: 'Hunza', second: 'Skardu', third: 'Islamabad' },
    };
  }
  return {
    first: 'Main Destination',
    second: 'Second City (Optional)',
    third: 'Third City (Optional)',
    defaults: { first: 'Dubai', second: 'Istanbul', third: 'Antalya' },
  };
};

/** Defaults applied when user picks a product line card */
export const getProductLineDefaults = (productLine) => {
  const vis = getServiceVisibility(productLine);
  const labels = getStayStopLabels(productLine);
  const baseServices = {
    includeFlight: !!vis.defaultIncludeFlight,
    includeHotel: vis.defaultIncludeHotel !== false,
    includeTransport: true,
    includeHomePickup: !!vis.defaultIncludeHomePickup,
    includeVisa: !!vis.defaultIncludeVisa,
    includeCarRental: !!vis.defaultIncludeCarRental,
    includeZiaraat: !!vis.defaultIncludeZiaraat,
  };

  const destinationCountry = getDefaultDestinationCountry(productLine);

  if (productLine === 'Umrah') {
    return {
      productLine: 'Umrah',
      journeyType: 'umrah',
      destinationCountry,
      ...baseServices,
      firstEntryLocation: labels.defaults.first,
      firstEntryNights: 5,
      secondEntryLocation: labels.defaults.second,
      secondEntryNights: 5,
      thirdEntryLocation: labels.defaults.third,
      thirdEntryNights: 3,
      includeFirstEntry: true,
      includeSecondEntry: true,
      includeThirdEntry: false,
      visaType: 'Umrah',
      flightDestination: 'JED',
      flightOrigin: 'ISB',
      makkahZiaraat: 'Standard',
      madinaZiaraat: 'Standard',
      transportationType: 'Private Car',
      packageType: '5 Star',
    };
  }

  if (productLine === 'Domestic') {
    return {
      productLine: 'Domestic',
      journeyType: 'travel',
      destinationCountry,
      ...baseServices,
      firstEntryLocation: labels.defaults.first,
      firstEntryNights: 3,
      secondEntryLocation: labels.defaults.second,
      secondEntryNights: 2,
      thirdEntryLocation: labels.defaults.third,
      thirdEntryNights: 1,
      includeFirstEntry: true,
      includeSecondEntry: false,
      includeThirdEntry: false,
      visaType: 'Tourist',
      includeVisa: false,
      includeZiaraat: false,
      makkahZiaraat: 'None',
      madinaZiaraat: 'None',
      flightDestination: 'ISB',
      flightOrigin: 'LHE',
      transportationType: 'Private Car',
      packageType: '3 Star',
    };
  }

  if (productLine === 'Local') {
    return {
      productLine: 'Local',
      journeyType: 'travel',
      destinationCountry,
      ...baseServices,
      firstEntryLocation: labels.defaults.first,
      firstEntryNights: 2,
      secondEntryLocation: labels.defaults.second,
      secondEntryNights: 1,
      thirdEntryLocation: labels.defaults.third,
      thirdEntryNights: 1,
      includeFirstEntry: true,
      includeSecondEntry: false,
      includeThirdEntry: false,
      visaType: 'Tourist',
      includeVisa: false,
      includeZiaraat: false,
      includeFlight: false,
      makkahZiaraat: 'None',
      madinaZiaraat: 'None',
      flightDestination: 'ISB',
      flightOrigin: 'ISB',
      transportationType: 'Private Car',
      packageType: 'Economy',
    };
  }

  // International
  return {
    productLine: 'International',
    journeyType: 'travel',
    destinationCountry,
    ...baseServices,
    firstEntryLocation: labels.defaults.first,
    firstEntryNights: 4,
    secondEntryLocation: labels.defaults.second,
    secondEntryNights: 3,
    thirdEntryLocation: labels.defaults.third,
    thirdEntryNights: 2,
    includeFirstEntry: true,
    includeSecondEntry: false,
    includeThirdEntry: false,
    visaType: 'Tourist',
    includeZiaraat: false,
    makkahZiaraat: 'None',
    madinaZiaraat: 'None',
    flightDestination: 'DXB',
    flightOrigin: 'ISB',
    transportationType: 'Private Car',
    packageType: '4 Star',
  };
};
